import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { SiteHeader } from './components/SiteHeader';
import Home from './pages/Home';
import Auth from './pages/Auth';
import CustomQuote from './pages/CustomQuote';
import HubstopQuote from './pages/HubstopQuote';
import YearlyQuote from './pages/YearlyQuote';
import SummerCampQuote from './pages/SummerCampQuote';
import Routes_ from './pages/Routes';
import Dashboard from './pages/Dashboard';
import ShuttleInfo from './pages/ShuttleInfo';
import CertifiedDrivers from './pages/CertifiedDrivers';
import Contact from './pages/Contact';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Terms from './pages/Terms';
import ReferralTerms from './pages/ReferralTerms';
import RouteOffersList from './pages/admin/RouteOffersList';
import RouteOfferDetail from './pages/admin/RouteOfferDetail';
import CreateRouteOffer from './pages/admin/CreateRouteOffer';
import DebugOffer from './pages/admin/DebugOffer';
import QuoteRequestsList from './pages/admin/QuoteRequestsList';
import QuoteRequestDetail from './pages/admin/QuoteRequestDetail';
import BulkCreateOffers from './pages/admin/BulkCreateOffers';
import StripeWebhooks from './pages/admin/StripeWebhooks';
import SystemTools from './pages/admin/SystemTools';
import WishIntelligence from './pages/admin/WishIntelligence';
import WishImport from './pages/admin/WishImport';
import Organizations from './pages/admin/Organizations';
import OrganizationDetail from './pages/admin/OrganizationDetail';
import SCHSummerReport from './pages/SCHSummerReport';
import SchoolReportPage from './pages/SchoolReportPage';
import OfferDetail from './pages/OfferDetail';
import OfferSuccess from './pages/OfferSuccess';
import OfferCancel from './pages/OfferCancel';

// Protected route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-shuttlebee-yellow border-t-transparent rounded-full animate-spin" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  return <>{children}</>;
}

// Admin-only route wrapper
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, role, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-shuttlebee-yellow border-t-transparent rounded-full animate-spin" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  if (role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

export default function App() {
  return (
    <div className="min-h-screen bg-gray-50">
      <SiteHeader />
      <main>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<Home />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/register" element={<Auth />} />
          <Route path="/routes" element={<Routes_ />} />
          <Route path="/shuttle-info" element={<ShuttleInfo />} />
          <Route path="/certified-drivers" element={<CertifiedDrivers />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/sch-summer-2026" element={<AdminRoute><SCHSummerReport /></AdminRoute>} />
          <Route path="/referral-terms" element={<ReferralTerms />} />

          {/* Protected routes */}
          <Route
            path="/custom-quote"
            element={
              <ProtectedRoute>
                <CustomQuote />
              </ProtectedRoute>
            }
          />
          <Route
            path="/hubstop-quote"
            element={
              <ProtectedRoute>
                <HubstopQuote />
              </ProtectedRoute>
            }
          />
          <Route
            path="/yearly-quote"
            element={
              <ProtectedRoute>
                <YearlyQuote />
              </ProtectedRoute>
            }
          />
          <Route
            path="/summer-camp-quote"
            element={
              <ProtectedRoute>
                <SummerCampQuote />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/offers/:offerId"
            element={
              <ProtectedRoute>
                <OfferDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/offers/:offerId/success"
            element={
              <ProtectedRoute>
                <OfferSuccess />
              </ProtectedRoute>
            }
          />
          <Route
            path="/offers/:offerId/cancel"
            element={
              <ProtectedRoute>
                <OfferCancel />
              </ProtectedRoute>
            }
          />

          {/* Admin routes */}
          <Route
            path="/admin/route-offers"
            element={
              <AdminRoute>
                <RouteOffersList />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/route-offers/create"
            element={
              <AdminRoute>
                <CreateRouteOffer />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/route-offers/:id"
            element={
              <AdminRoute>
                <RouteOfferDetail />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/route-offers/:id/edit"
            element={
              <AdminRoute>
                <CreateRouteOffer />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/quote-requests"
            element={
              <AdminRoute>
                <QuoteRequestsList />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/quote-requests/:id"
            element={
              <AdminRoute>
                <QuoteRequestDetail />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/bulk-create-offers"
            element={
              <AdminRoute>
                <BulkCreateOffers />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/stripe-webhooks"
            element={
              <AdminRoute>
                <StripeWebhooks />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/wish-intelligence"
            element={
              <AdminRoute>
                <WishIntelligence />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/wish-import"
            element={
              <AdminRoute>
                <WishImport />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/debug-offer"
            element={
              <AdminRoute>
                <DebugOffer />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/system-tools"
            element={
              <AdminRoute>
                <SystemTools />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/organizations"
            element={
              <AdminRoute>
                <Organizations />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/organizations/:id"
            element={
              <AdminRoute>
                <OrganizationDetail />
              </AdminRoute>
            }
          />

          {/* School/camp report pages — access controlled by code */}
          <Route path="/school/:slug" element={<SchoolReportPage />} />

          {/* Catch-all redirect */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}
